import React from 'react';

import {Skeleton} from '@albert-io/atomic';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {useClassContentRevealed, useReportClassContentReady} from '../useClassContentReady';
import {ClassSettingsForm} from './ClassSettingsForm';
import {useClassSettings} from './useClassSettings';

interface Props {
  classroomId: string;
}

// Matches the loaded form (header row, labeled fields, a larger section) so the reveal fills in place.
const SettingsSkeleton = () => (
  <div aria-busy='true' className='pt-x4'>
    <span className='sr-only' role='status'>
      Loading settings…
    </span>
    <div className='gap-x3 m-auto flex max-w-[600px] flex-col'>
      <div aria-hidden='true' className='pb-x2 flex items-center justify-between'>
        <Skeleton className='h-x4 w-[180px] rounded-full' />
        <Skeleton className='h-x5 w-x16 rounded-full' />
      </div>
      {['f1', 'f2', 'f3'].map((key) => (
        <div aria-hidden='true' className='gap-x0-half flex flex-col' key={key}>
          <Skeleton className='h-x2 w-[140px] rounded-full' />
          <Skeleton className='rounded-x1 h-[52px] w-full' />
        </div>
      ))}
      <Skeleton className='rounded-x1 h-[220px] w-full' />
    </div>
  </div>
);

// v2 Settings tab: owns the classroom subscription + mutations (useClassSettings) and renders the
// RHF - Replaces the legacy Flux-store TeacherClassroomSettings.
export const Settings = ({classroomId}: Props) => {
  const settings = useClassSettings(classroomId);

  // Report when settings are ready (or error); the shell reveals the header + this form together on
  // `revealed`. The `!isReady` keeps the tab's own skeleton for post-reveal reloads (header stays).
  const revealed = useClassContentRevealed();
  useReportClassContentReady(settings.isReady);

  if (!revealed || !settings.isReady) {
    return <SettingsSkeleton />;
  }

  if (settings.hasError || typeof settings.classroom?.getId !== 'function') {
    return (
      <div className='pt-x4'>
        <ErrorRetryState copy='We couldn’t load this class’s settings.' onRetry={settings.reload} />
      </div>
    );
  }

  return (
    <div className='pt-x4'>
      <ClassSettingsForm settings={settings} />
    </div>
  );
};
